import React, { useRef, useState, useMemo } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./../App.css";

// Import SVG files
import cplus from "../assets/img/cplus.svg";
import java from "../assets/img/java.svg";
import csharp from "../assets/img/csharp.svg";
import cmake from "../assets/img/cmake.svg";
import nodejs from "../assets/img/nodejs.svg";
import reactjs from "../assets/img/reactjs.svg";
import unity from "../assets/img/unity.svg";
import mysql from "../assets/img/mysql.svg";
import mongodb from "../assets/img/mongodb.svg";
import python from "../assets/img/python.svg";
import flask from "../assets/img/flask.svg";
import tensorflow from "../assets/img/tensorflow.svg";
import firebase from "../assets/img/firebase.svg";
import docker from "../assets/img/docker.svg";
import css from "../assets/img/css.svg";

import googleCloud from "../assets/img/googleCloud.svg";
import drawio from "../assets/img/drawio.png";
import aws from "../assets/img/aws.svg";

import figma from "../assets/img/figma.svg";



const skillsData = [
    // Frontend
    { name: "CSS3", icon: css, category: "frontend", level: 80 },
    { name: "React", icon: reactjs, category: "frontend", level: 90 },
    { name: "Figma", icon: figma, category: "frontend", level: 68 },

    // Backend
    
    { name: "C#", icon: csharp, category: "backend", level: 82 },      
    { name: "Python", icon: python, category: "backend", level: 90 },
    { name: "Node.js", icon: nodejs, category: "backend", level: 87 },
    { name: "Java", icon: java, category: "backend", level: 85 },
    { name: "Flask", icon: flask, category: "backend", level: 85 },

    // Systems
    { name: "C++", icon: cplus, category: "systems", level: 78 },


    // AI
    { name: "Python", icon: python, category: "ai", level: 90 },
    { name: "TensorFlow", icon: tensorflow, category: "ai", level: 88 },

    // Database
    { name: "MySQL", icon: mysql, category: "database", level: 80 },
    { name: "MongoDB", icon: mongodb, category: "database", level: 80 },

    // Cloud
    { name: "Firebase", icon: firebase, category: "cloud", level: 80 },
    { name: "Google Cloud", icon: googleCloud, category: "cloud", level: 80 },
    { name: "Amazon Web Services (AWS)", icon: aws, category: "cloud", level: 75 },

    // DevOps
    { name: "Docker", icon: docker, category: "devops", level: 80 },
    { name: "Draw.io", icon: drawio, category: "devops", level: 85 },

    // Game
    { name: "Unity", icon: unity, category: "Game", level: 85 },


    // Build
    { name: "CMake", icon: cmake, category: "build", level: 60 },
];

const skillFilters = [
    { id: "frontend", label: "Frontend", color: "#EC4899" },
    { id: "backend", label: "Backend", color: "#10B981" },
    { id: "ai", label: "AI", color: "#5865F2" },
    { id: "cloud", label: "Cloud", color: "#F97316" },
];

// Color mapping for skill categories
const skillColorHex = {
    frontend: "#EC4899",
    backend: "#10B981", 
    ai: "#5865F2",
    cloud: "#F97316",
    systems: "#F59E0B",
    database: "#8B5CF6",
    devops: "#06B6D4",
    game: "#EF4444",
    build: "#84CC16"
};

const hexToRgba = (hex = "#000000", alpha = 1) => {
    const h = hex.replace("#", "");
    const bigint = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const Skills = ({ language }) => {
    const sliderRef = useRef(null);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [selectedFilters, setSelectedFilters] = useState([]);

    const displayedSkills = useMemo(() => {
        if (selectedFilters.length === 0) {
            return skillsData;
        }
        return skillsData.filter(skill => 
            selectedFilters.includes(skill.category.toLowerCase())
        );
    }, [selectedFilters]);

    const toggleFilter = (filterId) => {
        setSelectedFilters(prev => {
            const found = prev.includes(filterId);
            if (found) return prev.filter(id => id !== filterId);
            return [...prev, filterId];
        });
    };

    const settings = {
        infinite: displayedSkills.length > 4,
        speed: 600,
        slidesToShow: Math.min(4, displayedSkills.length),
        slidesToScroll: 1,
        autoplay: displayedSkills.length > 4,
        autoplaySpeed: 3000,
        cssEase: "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
        arrows: true,
        dots: false,
        pauseOnHover: true,
        beforeChange: (current, next) => setCurrentSlide(next),
        responsive: [
            {
                breakpoint: 1200,
                settings: {
                    slidesToShow: Math.min(3, displayedSkills.length),
                    slidesToScroll: 1,
                }
            },
            {
                breakpoint: 900,
                settings: {
                    slidesToShow: Math.min(2, displayedSkills.length),
                    slidesToScroll: 1,
                }
            },
            {
                breakpoint: 600,
                settings: {
                    slidesToShow: Math.min(2, displayedSkills.length),
                    slidesToScroll: 1,
                    arrows: false,
                }
            }
        ]
    };

    return (
        <section className="skills-section" id="skills">
            <div className="cyber-background">
                <div className="cyber-grid"></div>
                <div className="floating-particles">
                    {[...Array(40)].map((_, i) => (
                        <div key={i} className={`particle particle-${i % 4 + 1}`}></div>
                    ))}
                </div>

            </div>

            <div className="skills-container">
                {/* Header Section */}
                <div className="skills-header">
                    <div className="header-content">
                        {/*
                        <div className="skill-counter">
                            <span className="counter-number">{skillsData.length}</span>
                            <span className="counter-label">Different Tools</span>
                        </div>
                         */}
                        <div className="title-section">
                            <h2 className="main-title">
                                <span className="title-accent">{language === 'es' ? 'HABILIDADES' : 'TECH'}</span>
                                <span className="title-accent">{language === 'es' ? 'TÉCNICAS' : 'STACK'}</span>
                                <div className="title-underline"></div>
                            </h2>
                        </div>
                    </div>
                </div>

                {/* Skills Carousel */}
                <div className="carousel-section">
                    <div className="carousel-wrapper">
                        <Slider {...settings} ref={sliderRef} className="skills-carousel">
                            {displayedSkills.map((skill, index) => {
                                // Determine which color to show:
                                // - if no filter selected: show skill's category color
                                // - if one or more filters selected: highlight with the first selected filter the skill matches
                                const skillCategory = skill.category.toLowerCase();
                                let colorHex = null;
                                if (selectedFilters.length === 0) {
                                    colorHex = skillColorHex[skillCategory];
                                } else {
                                    const firstSelectedMatch = selectedFilters.find((f) => f === skillCategory);
                                    if (firstSelectedMatch) colorHex = skillColorHex[firstSelectedMatch];
                                }

                                // If filters are selected and this skill does NOT match any selected, dim it
                                const dimWhenFiltering = selectedFilters.length > 0 && !selectedFilters.includes(skillCategory);

                                const cardStyle = colorHex
                                    ? {
                                          borderTop: `4px solid ${colorHex}`,
                                          boxShadow: `0 8px 24px ${hexToRgba(colorHex, 0.10)}`,
                                          backgroundClip: "padding-box",
                                      }
                                    : {};

                                const wrapperStyle = {
                                    padding: 8,
                                    opacity: dimWhenFiltering ? 0.45 : 1,
                                    transition: "opacity 220ms ease",
                                };

                                return (
                                    <div key={index} className="skill-slide" style={wrapperStyle}>
                                        <div style={{ borderRadius: 20, overflow: "hidden", ...cardStyle }}>
                                            <div className={`skill-card ${index === currentSlide ? 'active' : ''}`}>
                                                {/* Icon Section */}
                                                <div className="icon-section">
                                                    <div className="icon-container">
                                                        <div className="icon-backdrop"></div>
                                                        <div className="icon-wrapper">
                                                            <img src={skill.icon} alt={skill.name} className="skill-icon" />
                                                        </div>
                                                        <div className="icon-glow"></div>
                                                    </div>
                                                </div>

                                                {/* Card Footer */}
                                                <div className="card-footer">
                                                    <h3 className="skill-name">{skill.name}</h3>
                                                </div>

                                                {/* Hover Effects */}
                                                <div className="card-overlay"></div>
                                                <div className="scan-line"></div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </Slider>
                    </div>
                </div>

                {/* Filter Section */}
                <div className="stats-section">
                    {skillFilters.map((filter) => (
                        <div
                            key={filter.id}
                            className={`stat-item ${selectedFilters.includes(filter.id) ? "active-stat" : ""}`}
                            onClick={() => toggleFilter(filter.id)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") toggleFilter(filter.id);
                            }}
                            style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}
                        >
                            <div
                                style={{
                                    width: 16,
                                    height: 16,
                                    borderRadius: 8,
                                    background: selectedFilters.includes(filter.id) ? filter.color : "transparent",
                                    border: selectedFilters.includes(filter.id) ? "none" : "2px solid rgba(255,255,255,0.08)",
                                }}
                            />
                            <div className="stat-content">
                                <span className="stat-number">{filter.label}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};